import axios from 'axios'
import { getFirebaseToken } from '../auth/CognitoAuth'
import { APP_CONFIG } from '../config'

const API_BASE_URL = APP_CONFIG.apiUrl || 'http://localhost:8000/api/v1'

// Global demo mode state (set by App component)
let globalDemoMode = false

export function setDemoMode(enabled: boolean) {
  globalDemoMode = enabled
}

export function getDemoMode() {
  return globalDemoMode
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token and demo mode header to requests
apiClient.interceptors.request.use(async (config) => {
  const token = await getFirebaseToken()
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  if (globalDemoMode) {
    config.headers['X-Demo-Mode'] = 'true'
  }
  return config
}, (error) => {
  return Promise.reject(error)
})

// Specificity Map APIs
export const api = {
  // Specificity
  async getAvailableDiseases(): Promise<string[]> {
    const { data } = await apiClient.get('/specificity/diseases')
    return data
  },

  async getCellTypes(disease: string) {
    const { data } = await apiClient.get('/specificity/cell-types', {
      params: { disease },
    })
    return data
  },

  async analyzeSpecificity(params: {
    disease: string
    tissue?: string
    cell_type?: string
    disease_cell_types?: string[]
    control_cell_types?: string[]
    min_log_fc?: number
    max_pvalue?: number
    dataset_id?: string
  }) {
    const { data } = await apiClient.post('/specificity/analyze', params)
    return data
  },

  async getHeatmapData(analysisId: string) {
    const { data } = await apiClient.get(`/specificity/heatmap/${analysisId}`)
    return data
  },

  async getLncRNAExpression(disease: string, lncrna: string) {
    const { data } = await apiClient.get('/specificity/lncrna-expression', {
      params: { disease, lncrna },
    })
    return data
  },

  // NEW: Fast cached cell types endpoint
  async getCachedCellTypes(disease: string) {
    const { data} = await apiClient.get('/specificity/cell-types-cached', {
      params: { disease },
    })
    return data
  },

  // NEW: Get available datasets for a disease
  async getDatasetsForDisease(disease: string) {
    try {
      const { data } = await apiClient.get('/datasets', {
        params: { disease },
      })
      return data
    } catch (error) {
      console.error('Failed to fetch datasets:', error)
      // Return empty array on error
      return []
    }
  },

  // NEW: Correlation-based analysis
  async analyzeCorrelation(params: {
    disease: string
    cell_types: string[]
    max_cells?: number
    n_genes?: number
    top_n_lncrnas?: number
    n_clusters?: number
    dataset_id?: string
    use_prioritization?: boolean
  }) {
    const queryParams = new URLSearchParams({
      disease: params.disease,
      max_cells: (params.max_cells || 20000).toString(),
      n_genes: (params.n_genes || 1000).toString(),
      top_n_lncrnas: (params.top_n_lncrnas || 1000).toString(),
      n_clusters: (params.n_clusters || 5).toString(),
      use_prioritization: (params.use_prioritization !== false).toString(),
    })

    // Add dataset_id if provided
    if (params.dataset_id) {
      queryParams.set('dataset_id', params.dataset_id)
    }

    // Add cell types as multiple params
    params.cell_types.forEach(ct => {
      queryParams.append('cell_types', ct)
    })

    const { data } = await apiClient.post(
      `/specificity/analyze-correlation?${queryParams}`
    )
    return data
  },

  // NEW: UMAP-based specificity analysis
  async analyzeUMAP(params: {
    disease: string
    cell_types: string[]
    max_cells?: number
    n_clusters?: number
    top_n_lncrnas?: number
    n_genes?: number
    dataset_id?: string
  }) {
    const queryParams = new URLSearchParams({
      disease: params.disease,
      max_cells: (params.max_cells || 500).toString(),
      n_clusters: (params.n_clusters || 5).toString(),
      top_n_lncrnas: (params.top_n_lncrnas || 30).toString(),
      n_genes: (params.n_genes || 1000).toString(),
    })

    // Add dataset_id if provided
    if (params.dataset_id) {
      queryParams.set('dataset_id', params.dataset_id)
    }

    // Add cell types as multiple params
    params.cell_types.forEach(ct => {
      queryParams.append('cell_types', ct)
    })

    const { data } = await apiClient.post(
      `/specificity/analyze-umap?${queryParams}`
    )
    return data
  },

  async getExpressionForGRN(disease: string, dataset_id?: string) {
    const { data } = await apiClient.get('/specificity/expression-for-grn', {
      params: { disease, dataset_id },
    })
    return data
  },

  // Expression Statistics (Precomputed)
  async getExpressionStats(params: {
    disease: string
    dataset_id?: string
    lncrnas?: string[]
    cell_types?: string[]
    min_log2fc?: number
    max_adj_pvalue?: number
    top_n?: number
  }) {
    const { data } = await apiClient.post('/expression-stats/query', params)
    return data
  },

  async getExpressionStatsSummary(disease: string, dataset_id?: string) {
    const url = dataset_id
      ? `/expression-stats/summary/${disease}/${dataset_id}`
      : `/expression-stats/summary/${disease}`
    const { data } = await apiClient.get(url)
    return data
  },

  async getExpressionStatsCellTypes(disease: string, dataset_id?: string) {
    const url = dataset_id
      ? `/expression-stats/cell-types/${disease}/${dataset_id}`
      : `/expression-stats/cell-types/${disease}`
    const { data } = await apiClient.get(url)
    return data
  },

  async checkExpressionStatsAvailable(disease: string, dataset_id?: string) {
    const url = dataset_id
      ? `/expression-stats/check/${disease}/${dataset_id}`
      : `/expression-stats/check/${disease}`
    const { data } = await apiClient.get(url)
    return data
  },

  // GRN
  async buildGRN(params: {
    gene_list: string[]
    disease?: string
    cluster_id?: number
    correlation_threshold?: number
    max_neighbors?: number
    include_pathways?: boolean
    expression_matrix?: number[][]
    gene_symbols?: string[]
    gene_log_fc?: Record<string, number>
    use_expression_correlation?: boolean
    dataset_id?: string
  }) {
    const { data } = await apiClient.post('/grn/build', params)
    return data
  },

  async listNetworks() {
    const { data } = await apiClient.get('/grn/networks')
    return data
  },

  async getNetwork(networkId: string) {
    const { data } = await apiClient.get(`/grn/network/${networkId}`)
    return data
  },

  async deleteNetwork(networkId: string) {
    const { data } = await apiClient.delete(`/grn/network/${networkId}`)
    return data
  },

  async buildPathwayGRNs(params: {
    gene_list: string[]
    disease?: string
    expression_matrix?: number[][]
    gene_symbols?: string[]
    gene_log_fc?: Record<string, number>
  }) {
    const { data } = await apiClient.post('/grn/build_pathway_grns', params)
    return data
  },

  async buildCorrelationGRN(params: {
    lncrna: string
    disease: string
    cell_types: string[]
    dataset_id?: string
    correlation_threshold?: number
    max_genes?: number
    expansion_layers?: number
    ppi_threshold?: number
  }) {
    const { data } = await apiClient.post('/grn/build-correlation-grn', params)
    return data
  },

  // Disease Info
  async getLncRNADiseaseConnections(params: {
    lncrna_symbols: string[]
    disease: string
    max_articles?: number
  }) {
    const { data } = await apiClient.post('/disease/lncrna-connections', params)
    return data
  },

  async getAssociatedDiseases(lncrnaSymbol: string): Promise<string[]> {
    const { data } = await apiClient.get(`/disease/lncrna/${lncrnaSymbol}/diseases`)
    return data
  },

  async searchPubMed(query: string, maxResults: number = 20) {
    const { data } = await apiClient.get('/disease/search/pubmed', {
      params: { query, max_results: maxResults },
    })
    return data
  },

  // Genes
  async getGeneInfo(geneSymbol: string) {
    const { data } = await apiClient.get(`/genes/${geneSymbol}`)
    return data
  },

  async findSimilarGenes(params: {
    query_gene: string
    top_n?: number
    biotype_filter?: string
  }) {
    const { data } = await apiClient.post('/genes/similar', params)
    return data
  },

  async getGeneExpression(geneSymbol: string) {
    const { data } = await apiClient.get(`/genes/${geneSymbol}/expression`)
    return data
  },

  async listLncRNAs(tissue?: string, minExpression?: number) {
    const { data } = await apiClient.get('/genes/lncrnas/list', {
      params: { tissue, min_expression: minExpression },
    })
    return data
  },

  // Chat (Note: Chat endpoints use /api/chat, not /api/v1/chat)
  async sendChatMessage(params: {
    message: string
    conversation_id?: string
    context?: any
    include_gene_data?: boolean
  }) {
    const baseUrl = API_BASE_URL.replace('/api/v1', '')
    const { data } = await axios.post(
      `${baseUrl}/api/chat/message`,
      params,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(globalDemoMode ? { 'X-Demo-Mode': 'true' } : {}),
        },
        withCredentials: true,
      }
    )
    return data
  },

  async getConversation(conversationId: string) {
    const baseUrl = API_BASE_URL.replace('/api/v1', '')
    const { data } = await axios.get(
      `${baseUrl}/api/chat/conversation/${conversationId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(globalDemoMode ? { 'X-Demo-Mode': 'true' } : {}),
        },
        withCredentials: true,
      }
    )
    return data
  },

  async deleteConversation(conversationId: string) {
    const baseUrl = API_BASE_URL.replace('/api/v1', '')
    const { data } = await axios.delete(
      `${baseUrl}/api/chat/conversation/${conversationId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(globalDemoMode ? { 'X-Demo-Mode': 'true' } : {}),
        },
        withCredentials: true,
      }
    )
    return data
  },

  // Expression Stage
  async getExpressionByStage(params: {
    gene_symbol: string
    disease: string
    tissue?: string
  }) {
    const { data } = await apiClient.post('/expression/by-stage', params)
    return data
  },

  // Health
  async healthCheck() {
    const { data } = await apiClient.get('/health')
    return data
  },

  // Knockdown Simulation
  async simulateKnockdown(params: {
    target_gene: string
    disease: string
    cell_types?: string[]
    dataset_id?: string
    include_rescue_score?: boolean
  }) {
    const { data } = await apiClient.post('/api/v1/knockdown/simulate', params)
    return data
  },

  // Enhanced GRN with both disease and normal data
  async buildEnhancedGRN(params: {
    gene_list: string[]
    disease: string
    cell_types: string[]
    dataset_id?: string
    correlation_threshold?: number
    include_normal_comparison?: boolean
  }) {
    const { data } = await apiClient.post('/grn/build-enhanced', params)
    return data
  },

  // Get biological insights for a target
  async getTargetInsights(params: {
    target_gene: string
    disease: string
    include_literature?: boolean
    include_pathways?: boolean
  }) {
    const { data } = await apiClient.post('/insights/target', params)
    return data
  },

  // Design ASO (antisense oligonucleotides) for RNA targeting
  async designASO(params: {
    target_gene: string
    top_n?: number
    min_length?: number
    max_length?: number
  }) {
    const { data } = await apiClient.post('/aso/design', params)
    return data
  },

  // Get dual rankings (expression + network priority)
  async getDualRankings(params: {
    disease: string
    dataset_id?: string
    top_n?: number
    cell_types?: string[]
    min_log2fc?: number
    max_adj_pvalue?: number
  }) {
    const { data } = await apiClient.post('/dual-ranking', params)
    return data
  },
}

// Error interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('API Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)
